import { useState } from 'react';
import { X, Ellipsis, Pencil, Trash2 } from '@/components/icons';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { DetailSkeleton } from '../ui/skeleton-presets';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '../ui/drawer';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
} from '../ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { useDocumentGetById, useDocumentDelete } from '@/lib/hooks';
import { DocumentFormDrawer } from './DocumentFormDrawer';
import { DocumentViewer } from '../DocumentViewer';
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin';
import type { DocumentFormOwner } from './types';

// What this drawer reads off the fetched document (oRPC hooks are untyped —
// known type-check baseline).
interface DocumentRecord {
  id: string;
  category: string;
  title: string;
  subType: string | null;
  documentNumber: string | null;
  issuer: string | null;
  version: string | null;
  expiryDate: Date | string | null;
  s3Key: string | null;
  fileName: string | null;
  crewId: string | null;
  passengerId: string | null;
  aircraftId: string | null;
}

interface ViewDocumentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string | null;
  readOnly?: boolean; // hides the Ellipsis menu entirely
  canManage?: boolean; // menu gate is (isAdmin || canManage) && !readOnly
  // When set, the menu shows "Remove" (calls this) instead of "Delete" —
  // used where the document is detached from a trip/flight, not deleted.
  onRemove?: () => Promise<void> | void;
}

// Unified read/detail drawer for every document category. The layout and the
// edit-drawer owner are derived from the fetched document itself; replaces the
// per-category View*DocumentDrawer components.
export function ViewDocumentDrawer({
  isOpen,
  onClose,
  documentId,
  readOnly = false,
  canManage = false,
  onRemove,
}: ViewDocumentDrawerProps) {
  const { data, isLoading } = useDocumentGetById(documentId);
  const doc = data as DocumentRecord | undefined;
  const deleteDocument = useDocumentDelete();
  const deleteDocumentAsync = deleteDocument.mutateAsync as unknown as (input: {
    id: string;
  }) => Promise<unknown>;
  const { isAdmin } = useIsAdmin();
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  if (!documentId) return null;

  const isCompliance = doc?.category === 'Compliance';

  // Owner for the embedded edit drawer, derived from the document's relations
  const editOwner: DocumentFormOwner = doc?.crewId
    ? { type: 'crew', crewId: doc.crewId }
    : doc?.passengerId
      ? { type: 'passenger', passengerId: doc.passengerId }
      : doc?.aircraftId
        ? { type: 'aircraft', aircraftId: doc.aircraftId }
        : { type: 'compliance' };

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return null;
    return dayjs(date).format('MMM D, YYYY');
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteDocumentAsync({ id: documentId });
      toast.success('Document deleted successfully');
      setIsDeleteDialogOpen(false);
      onClose();
    } catch {
      toast.error('Failed to delete document');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRemove = async () => {
    if (!onRemove) return;
    setIsRemoving(true);
    try {
      await onRemove();
      onClose();
    } catch {
      toast.error('Failed to remove document');
    } finally {
      setIsRemoving(false);
    }
  };

  const isExpired = doc?.expiryDate && dayjs(doc.expiryDate).isBefore(dayjs(), 'day');

  return (
    <>
      <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
          {/* Header */}
          <DrawerHeader className="flex items-center justify-between p-4">
            <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
              {doc?.category === 'Passenger' ? 'PAX Document Details' : 'Document Details'}
            </DrawerTitle>
            <div className="flex items-center gap-3">
              {(isAdmin || canManage) && !readOnly && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="icon" roundness="round" className="size-8">
                      <Ellipsis className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setIsEditDrawerOpen(true)}>
                      <Pencil className="w-4 h-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      disabled={isRemoving}
                      onClick={onRemove ? handleRemove : () => setIsDeleteDialogOpen(true)}
                    >
                      <Trash2 className="w-4 h-4" />
                      {onRemove ? 'Remove' : 'Delete'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </DrawerHeader>

          {/* Content */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4" style={{ WebkitOverflowScrolling: 'touch' }}>
            {isLoading ? (
              <DetailSkeleton rows={4} />
            ) : doc ? (
              <div className="flex flex-col gap-4">
                {/* Title + secondary line (document number, or version for compliance) */}
                <div className="bg-surface-L2 rounded-md px-3 py-2">
                  <p className="text-base font-semibold text-content-primary-bold">
                    {doc.title}
                  </p>
                  {(isCompliance ? doc.version : doc.documentNumber) && (
                    <p className="text-sm text-content-primary-subtle">
                      {isCompliance ? doc.version : doc.documentNumber}
                    </p>
                  )}
                </div>

                {/* Badges */}
                <div className="flex gap-3 items-start flex-wrap">
                  {isCompliance && doc.subType && (
                    <Badge variant="outline" className="bg-surface-L1 border-border-L2 text-content-primary-bold text-xs font-medium px-2 py-0.5 rounded-lg">
                      {doc.subType}
                    </Badge>
                  )}
                  {!isCompliance && doc.issuer && (
                    <Badge variant="outline" className="bg-surface-L0 border-neutral-300 text-neutral-950 text-xs font-medium px-2 py-0.5 rounded-lg">
                      {doc.issuer}
                    </Badge>
                  )}
                  {doc.expiryDate && (
                    isExpired ? (
                      <Badge variant="outline" className="border-border-negative-bold bg-background-negative-subtle text-content-negative-bold text-xs font-medium px-2 py-0.5 rounded-lg">
                        Expired {formatDate(doc.expiryDate)}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-surface-L0 border-neutral-300 text-neutral-950 text-xs font-medium px-2 py-0.5 rounded-lg">
                        Expires {formatDate(doc.expiryDate)}
                      </Badge>
                    )
                  )}
                </div>

                {/* Document preview (compliance keeps its viewer in the footer) */}
                {!isCompliance && (
                  <DocumentViewer s3Key={doc.s3Key} fileName={doc.fileName || doc.s3Key} />
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <p className="text-content-primary-subtle">Document not found</p>
              </div>
            )}
          </div>

          {isCompliance && doc && (
            <DrawerFooter>
              <DocumentViewer s3Key={doc.s3Key} fileName={doc.fileName || doc.s3Key} />
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>

      {/* Edit Drawer */}
      <DocumentFormDrawer
        isOpen={isEditDrawerOpen}
        onClose={() => setIsEditDrawerOpen(false)}
        documentId={documentId}
        owner={editOwner}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="p-8">
          <AlertDialogDescription className="text-center text-sm text-content-primary-subtle leading-5">
            Are you sure you want to delete this document?
          </AlertDialogDescription>
          <div className="flex flex-col gap-2 mt-2">
            <Button
              variant="destructive"
              size="mini"
              className="w-full"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              <Trash2 className="size-4" />
              Delete
            </Button>
            <Button
              variant="secondary"
              size="mini"
              className="w-full"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
